/**
 * useReconnect — surfaces the live wsClient transport state so UI layers
 * (the reconnect overlay, status pills, etc.) can react to network drops
 * without poking the lobbyStore directly.
 *
 * Returns the raw transport state plus debounced flags so the UI doesn't
 * flicker on brief reconnects. The overlay is only shown after the
 * `reconnecting` state has been stable for OVERLAY_DELAY_MS — short
 * Cloudflare WebSocket hiccups (~100-300ms) don't trigger a curtain
 * that the user can perceive.
 */

import { useEffect, useState } from 'react';
import { useWsState } from '../stores/lobbyStore';
import type { WsState } from '../net/wsClient';

/** Show the overlay only after a sustained reconnecting state. */
const OVERLAY_DELAY_MS = 1500;

export interface ReconnectStatus {
  /** Raw transport state from wsClient. */
  state: WsState;
  /** True when the socket has been reconnecting for > OVERLAY_DELAY_MS. */
  isReconnecting: boolean;
  /** True only on the very first connect attempt (initial 'connecting'). */
  isInitialConnecting: boolean;
}

export function useReconnect(): ReconnectStatus {
  const state = useWsState();
  const [debouncedReconnecting, setDebouncedReconnecting] = useState(false);

  useEffect(() => {
    if (state !== 'reconnecting') {
      setDebouncedReconnecting(false);
      return;
    }
    const t = window.setTimeout(() => {
      setDebouncedReconnecting(true);
    }, OVERLAY_DELAY_MS);
    return () => {
      window.clearTimeout(t);
    };
  }, [state]);

  return {
    state,
    isReconnecting: debouncedReconnecting,
    isInitialConnecting: state === 'connecting',
  };
}
